import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { Tooltip, Button, TextField, Box, Avatar } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { format, isSameDay, parseISO, startOfMonth } from "date-fns";
import { Can } from "../../components/Can";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";
import { canUserAccessKanban } from "../../utils/userPermissions";

const applyTicketStageMove = (ticketsList, ticketId, toLaneId, kanbanTags) => {
  const kanbanTagIds = new Set(kanbanTags.map((tag) => tag.id));
  const numericId = Number(ticketId);
  const toTagId = toLaneId === "lane0" ? null : Number(toLaneId);

  return ticketsList.map((ticket) => {
    if (ticket.id !== numericId) {
      return ticket;
    }

    const nonKanbanTags = (ticket.tags || []).filter((tag) => !kanbanTagIds.has(tag.id));

    if (!toTagId) {
      return { ...ticket, tags: nonKanbanTags };
    }

    const newTag = kanbanTags.find((tag) => tag.id === toTagId);
    if (!newTag) {
      return ticket;
    }

    return { ...ticket, tags: [...nonKanbanTags, newTag] };
  });
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(99, 102, 241, ${alpha})`;
  const normalized = hex.replace("#", "");
  const full =
    normalized.length === 3
      ? normalized.split("").map((c) => c + c).join("")
      : normalized;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getLaneGlassStyle = (color, isLight) => ({
  backgroundColor: hexToRgba(color, isLight ? 0.1 : 0.14),
  border: `1px solid ${hexToRgba(color, isLight ? 0.24 : 0.34)}`,
  borderTop: `3px solid ${color}`,
  borderRadius: 10,
  color: isLight ? "#334155" : "#e5e7eb",
  boxShadow: isLight
    ? "0 1px 3px rgba(15, 23, 42, 0.06)"
    : "0 1px 3px rgba(0, 0, 0, 0.22)",
});

const getCardStageStyle = (stageColor, isLight, isUnread) => {
  const defaultBg = isLight ? "#ffffff" : "#1f2937";

  if (!stageColor) {
    return isUnread
      ? {
          backgroundColor: defaultBg,
          borderLeft: "3px solid #22c55e",
          borderColor: "#86efac",
          boxShadow: "0 0 0 1px rgba(34, 197, 94, 0.2)",
        }
      : { backgroundColor: defaultBg };
  }

  return {
    backgroundColor: hexToRgba(stageColor, isLight ? 0.14 : 0.18),
    borderLeft: `3px solid ${stageColor}`,
    borderTop: `1px solid ${hexToRgba(stageColor, isLight ? 0.28 : 0.38)}`,
    borderRight: `1px solid ${hexToRgba(stageColor, isLight ? 0.28 : 0.38)}`,
    borderBottom: `1px solid ${hexToRgba(stageColor, isLight ? 0.28 : 0.38)}`,
    ...(isUnread
      ? { boxShadow: `0 0 0 1px ${hexToRgba(stageColor, 0.35)}` }
      : {}),
  };
};

const useStyles = makeStyles(theme => ({
  "@global": {
    ".smooth-dnd-ghost": {
      opacity: "0 !important",
      visibility: "hidden !important",
      pointerEvents: "none !important",
    },
    ".kanban-drag-preview": {
      position: "fixed",
      zIndex: 1400,
      pointerEvents: "none",
      margin: 0,
      transform: "none",
      boxSizing: "border-box",
      cursor: "grabbing",
    },
    ".kanban-drag-preview article": {
      margin: "0 !important",
      transform: "none !important",
      width: "288px !important",
      maxWidth: "288px !important",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.18) !important",
      cursor: "grabbing !important",
    },
    "body.kanban-dragging .kanban-scroll-container": {
      touchAction: "none",
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    flex: 1,
    minHeight: 0,
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
    overscrollBehavior: "none",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    backgroundColor: theme.palette.fancyBackground,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    width: "100%",
    flexShrink: 0,
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(0.75),
      gap: theme.spacing(0.5),
    },
  },
  toolbarFilters: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
      "& .MuiFormControl-root": {
        marginRight: "0 !important",
      },
      "& .MuiOutlinedInput-root": {
        fontSize: "0.8125rem",
      },
      "& .MuiInputLabel-outlined": {
        fontSize: "0.8125rem",
      },
      "& .MuiButton-root": {
        padding: "4px 10px",
        fontSize: "0.75rem",
        minHeight: 32,
        lineHeight: 1.2,
      },
    },
  },
  dateInput: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      flex: "1 1 128px",
      minWidth: 118,
      maxWidth: 150,
      "& .MuiOutlinedInput-input": {
        padding: "7px 8px",
      },
    },
  },
  searchButton: {
    [theme.breakpoints.down("sm")]: {
      flexShrink: 0,
      minWidth: "auto",
    },
  },
  addColumnsButton: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  kanbanScrollContainer: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    overscrollBehaviorX: "contain",
    touchAction: "pan-x",
    WebkitOverflowScrolling: "touch",
    ...theme.scrollbarStyles,
  },
  kanbanColumns: {
    width: "max-content",
    minWidth: "100%",
    flex: 1,
    height: "100%",
    minHeight: 0,
    "& .react-trello-board": {
      width: "100% !important",
      height: "100% !important",
      maxHeight: "100% !important",
      minHeight: "0 !important",
      overflow: "hidden !important",
      display: "flex !important",
      flexDirection: "row !important",
      alignItems: "stretch !important",
      backgroundColor: "transparent !important",
      padding: "0 !important",
    },
    "& > div": {
      width: "100% !important",
      minWidth: "100% !important",
      height: "100% !important",
      maxHeight: "100% !important",
      minHeight: "0 !important",
      overflow: "hidden !important",
      display: "flex !important",
      flexDirection: "row !important",
      alignItems: "stretch !important",
      backgroundColor: "transparent !important",
    },
    "& .smooth-dnd-container.horizontal": {
      display: "flex !important",
      flexDirection: "row !important",
      flexWrap: "nowrap !important",
      alignItems: "stretch !important",
      gap: 8,
      width: "max-content !important",
      minWidth: "100% !important",
      height: "100% !important",
      maxHeight: "100% !important",
      minHeight: "0 !important",
      overflow: "hidden !important",
      whiteSpace: "normal !important",
    },
    "& .smooth-dnd-container.horizontal .smooth-dnd-draggable-wrapper": {
      flex: "0 0 300px !important",
      width: "300px !important",
      minWidth: "300px !important",
      maxWidth: "300px !important",
      height: "100% !important",
      maxHeight: "100% !important",
      alignSelf: "stretch !important",
      display: "flex !important",
      flexDirection: "column !important",
      overflow: "hidden !important",
      marginRight: "0 !important",
      [theme.breakpoints.down("xs")]: {
        flex: "0 0 300px !important",
        width: "300px !important",
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },
    "& .react-trello-lane": {
      width: "100% !important",
      height: "100% !important",
      minHeight: "0 !important",
      maxHeight: "100% !important",
      margin: "0 !important",
      padding: "10px 6px !important",
      boxSizing: "border-box",
      display: "flex !important",
      flexDirection: "column !important",
      overflow: "hidden !important",
      alignSelf: "stretch !important",
      backgroundColor:
        theme.mode === "light"
          ? "rgba(255, 255, 255, 0.42) !important"
          : "rgba(255, 255, 255, 0.04) !important",
      border: `1px solid ${
        theme.mode === "light"
          ? "rgba(148, 163, 184, 0.28)"
          : "rgba(148, 163, 184, 0.18)"
      } !important`,
      borderTop: `3px solid ${
        theme.mode === "light" ? "#94a3b8" : "#64748b"
      } !important`,
      borderRadius: "10px !important",
      boxShadow:
        theme.mode === "light"
          ? "0 1px 3px rgba(15, 23, 42, 0.06) !important"
          : "0 1px 3px rgba(0, 0, 0, 0.22) !important",
    },
    "& .react-trello-lane > header": {
      flexShrink: 0,
      marginBottom: 4,
      backgroundColor: "transparent !important",
      "& span, & div": {
        color: theme.mode === "light" ? "#334155" : "#e5e7eb",
        fontWeight: 600,
      },
    },
    "& .react-trello-lane > div": {
      flex: "1 1 0 !important",
      display: "flex !important",
      flexDirection: "column !important",
      justifyContent: "flex-start !important",
      alignItems: "stretch !important",
      alignSelf: "stretch !important",
      width: "100% !important",
      minWidth: "0 !important",
      minHeight: "0 !important",
      maxHeight: "100% !important",
      height: "auto !important",
      marginTop: "0 !important",
      overflowY: "auto !important",
      overflowX: "hidden !important",
      ...theme.scrollbarStylesSoft,
    },
    "& .react-trello-lane .smooth-dnd-container.vertical": {
      display: "flex !important",
      flexDirection: "column !important",
      justifyContent: "flex-start !important",
      alignItems: "center !important",
      flex: "0 1 auto !important",
      width: "100% !important",
      minHeight: "0 !important",
      height: "auto !important",
      paddingBottom: 4,
      boxSizing: "border-box",
      gap: 8,
    },
    "& .react-trello-lane .smooth-dnd-container.vertical .smooth-dnd-draggable-wrapper": {
      height: "auto !important",
      flex: "0 0 auto !important",
      width: "100% !important",
      maxWidth: "100% !important",
      margin: "0 !important",
      display: "flex !important",
      justifyContent: "center !important",
    },
    "& .react-trello-lane article": {
      minHeight: "auto !important",
      maxWidth: "288px !important",
      minWidth: "0 !important",
      width: "288px !important",
      borderBottom: `1px solid ${theme.mode === "light" ? "#e5e7eb" : "#374151"} !important`,
      padding: "8px 10px !important",
      margin: "0 auto !important",
      overflow: "visible !important",
      borderRadius: "8px !important",
      border: `1px solid ${theme.mode === "light" ? "#e5e7eb" : "#374151"}`,
      boxShadow:
        theme.mode === "light"
          ? "0 1px 2px rgba(0, 0, 0, 0.05)"
          : "0 1px 2px rgba(0, 0, 0, 0.25)",
      cursor: "grab",
      boxSizing: "border-box",
    },
    "& .kanban-card-dragging": {
      opacity: "1 !important",
    },
    "& .smooth-dnd-ghost article": {
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.18) !important",
      cursor: "grabbing !important",
      backgroundColor: `${theme.mode === "light" ? "#ffffff" : "#1f2937"} !important`,
    },
    "& .smooth-dnd-container.vertical .smooth-dnd-draggable-wrapper[style*='opacity']": {
      opacity: "0.35 !important",
    },
    "& .react-trello-lane article header": {
      display: "flex !important",
      justifyContent: "space-between !important",
      alignItems: "flex-start !important",
      width: "100% !important",
      gap: 6,
      borderBottom: "none !important",
      padding: "0 !important",
      marginBottom: "6px !important",
      boxSizing: "border-box",
      overflow: "visible !important",
    },
    "& .react-trello-lane article header > span:first-child": {
      flex: "1 1 0% !important",
      minWidth: "0 !important",
      width: "auto !important",
      maxWidth: "58% !important",
      overflow: "hidden !important",
      fontSize: "13px !important",
      lineHeight: "1.25 !important",
    },
    "& .react-trello-lane article header > span:nth-child(2)": {
      flex: "0 0 auto !important",
      width: "auto !important",
      minWidth: "88px !important",
      maxWidth: "42% !important",
      paddingRight: "2px !important",
      paddingLeft: "6px !important",
      textAlign: "right !important",
      fontSize: "11px !important",
      lineHeight: "1.25 !important",
    },
    "& .react-trello-lane article > div:last-child": {
      marginTop: 0,
      paddingTop: 0,
    },
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    flex: 1,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.mode === "light" ? "#111827" : "#f3f4f6",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardMeta: {
    fontSize: 10,
    color: theme.mode === "light" ? "#8a8f98" : "#9ca3af",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  cardMetaColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 1,
    flexShrink: 0,
    minWidth: 68,
    maxWidth: "100%",
  },
  cardTicketId: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.mode === "light" ? "#64748b" : "#cbd5e1",
    whiteSpace: "nowrap",
  },
  cardMetaUnread: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.success.main,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  cardBody: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    minWidth: 0,
  },
  cardAvatar: {
    width: 34,
    height: 34,
    fontSize: 14,
    flexShrink: 0,
    backgroundColor: theme.mode === "light" ? "#e5e7eb" : "#374151",
    color: theme.mode === "light" ? "#374151" : "#f3f4f6",
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  cardMessage: {
    fontSize: 12,
    lineHeight: 1.35,
    color: theme.mode === "light" ? "#5f6673" : "#9ca3af",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  agentBadge: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1.2,
    padding: "2px 6px",
    borderRadius: 10,
    backgroundColor: theme.mode === "light" ? "#111827" : "#374151",
    color: "#ffffff",
    textTransform: "uppercase",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ticketLink: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 8px",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    backgroundColor: theme.mode === "light" ? "#eef2ff" : "#1e3a5f",
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#e0e7ff" : "#1e40af",
    },
  },
  channelIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
}));

const getTimeLabel = (updatedAt) =>
  isSameDay(parseISO(updatedAt), new Date())
    ? format(parseISO(updatedAt), "HH:mm")
    : format(parseISO(updatedAt), "dd/MM/yyyy");

const KanbanCardBody = ({ ticket, classes, onView }) => (
  <div className={classes.cardBody}>
    <Avatar
      className={classes.cardAvatar}
      src={ticket.contact?.urlPicture || undefined}
      alt={ticket.contact?.name}
    >
      {ticket.contact?.name?.charAt(0)?.toUpperCase() || "?"}
    </Avatar>
    <div className={classes.cardContent}>
      <div className={classes.cardMessage} title={ticket.lastMessage || ticket.contact?.number}>
        {ticket.lastMessage || ticket.contact?.number || "—"}
      </div>
      <div className={classes.cardFooter}>
        {ticket?.user && (
          <span className={classes.agentBadge}>{ticket.user.name}</span>
        )}
        <button
          type="button"
          className={classes.ticketLink}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          Ver lead
        </button>
      </div>
    </div>
  </div>
);

const buildTicketCard = (ticket, classes, onView, renderChannelIcon, stageColor, isLight) => {
  const timeLabel = getTimeLabel(ticket.updatedAt);
  const isUnread = Number(ticket.unreadMessages) > 0;

  return {
    id: ticket.id.toString(),
    label: (
      <span className={classes.cardMetaColumn}>
        <span className={classes.cardTicketId}>Lead nº {ticket.id}</span>
        <span className={isUnread ? classes.cardMetaUnread : classes.cardMeta}>
          {timeLabel}
        </span>
      </span>
    ),
    title: (
      <span className={classes.cardTitleRow}>
        <Tooltip title={ticket.whatsapp?.name || ""}>
          <span className={classes.channelIcon}>{renderChannelIcon(ticket.channel)}</span>
        </Tooltip>
        <span className={classes.cardTitle}>{ticket.contact.name}</span>
      </span>
    ),
    description: (
      <KanbanCardBody
        ticket={ticket}
        classes={classes}
        onView={() => onView(ticket.uuid)}
      />
    ),
    draggable: true,
    href: "/tickets/" + ticket.uuid,
    style: getCardStageStyle(stageColor, isLight, isUnread),
  };
};

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLight = theme.mode === "light";
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [file, setFile] = useState({ lanes: [] });
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [applyDateFilter, setApplyDateFilter] = useState(false);
  const skipSocketFetchRef = useRef(false);

  const jsonString = user.queues.map(queue => queue.UserQueue.queueId);
  const dragPreviewStateRef = useRef({
    pointerX: 0,
    pointerY: 0,
    lastX: 0,
    lastY: 0,
    width: 288,
    height: 80,
    article: null,
    preview: null,
    rafId: null,
    isDragging: false,
  });

  const removeKanbanDragPreview = () => {
    const state = dragPreviewStateRef.current;
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    state.preview?.remove();
    state.preview = null;
    state.article = null;
    state.isDragging = false;
  };

  const updateKanbanDragPreview = (clientX, clientY) => {
    const state = dragPreviewStateRef.current;
    const preview = state.preview;
    if (!preview) {
      return;
    }

    preview.style.left = `${clientX - state.pointerX}px`;
    preview.style.top = `${clientY - state.pointerY}px`;
    preview.style.width = `${state.width}px`;
  };

  const ensureKanbanDragPreview = () => {
    const state = dragPreviewStateRef.current;
    if (state.preview || !state.article) {
      return;
    }

    const preview = document.createElement("div");
    preview.className = "kanban-drag-preview";
    const clone = state.article.cloneNode(true);
    clone.style.pointerEvents = "none";
    preview.appendChild(clone);
    document.body.appendChild(preview);
    state.preview = preview;
    updateKanbanDragPreview(state.lastX, state.lastY);
  };

  useEffect(() => {
    const isKanbanDragging = () =>
      document.body.classList.contains("smooth-dnd-disable-touch-action") ||
      document.body.classList.contains("kanban-dragging") ||
      Boolean(document.querySelector(".smooth-dnd-ghost"));

    const schedulePreviewPosition = (clientX, clientY) => {
      const state = dragPreviewStateRef.current;
      state.lastX = clientX;
      state.lastY = clientY;

      if (state.rafId) {
        return;
      }

      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        if (!state.isDragging) {
          return;
        }
        updateKanbanDragPreview(state.lastX, state.lastY);
      });
    };

    const onPointerDown = (event) => {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      const article = event.target.closest(".kanban-wrapper article");
      if (!article) {
        return;
      }

      const rect = article.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      const state = dragPreviewStateRef.current;

      state.article = article;
      state.pointerX = clientX - rect.left;
      state.pointerY = clientY - rect.top;
      state.lastX = clientX;
      state.lastY = clientY;
      state.width = rect.width || 288;
      state.height = rect.height || 80;
      state.isDragging = false;
    };

    const onPointerMove = (event) => {
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      const state = dragPreviewStateRef.current;

      if (!state.article) {
        return;
      }

      if (!state.isDragging && isKanbanDragging()) {
        state.isDragging = true;
        ensureKanbanDragPreview();
      }

      if (!state.isDragging) {
        return;
      }

      ensureKanbanDragPreview();
      schedulePreviewPosition(clientX, clientY);
    };

    const onPointerRelease = () => {
      removeKanbanDragPreview();
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, { capture: true, passive: true });
    document.addEventListener("mousemove", onPointerMove, true);
    document.addEventListener("touchmove", onPointerMove, { capture: true, passive: true });
    document.addEventListener("mouseup", onPointerRelease, true);
    document.addEventListener("touchend", onPointerRelease, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
      document.removeEventListener("mousemove", onPointerMove, true);
      document.removeEventListener("touchmove", onPointerMove, true);
      document.removeEventListener("mouseup", onPointerRelease, true);
      document.removeEventListener("touchend", onPointerRelease, true);
      removeKanbanDragPreview();
    };
  }, []);

  const cleanupStuckDrag = () => {
    document.body.classList.remove("kanban-dragging");
    document.body.classList.remove("smooth-dnd-disable-touch-action");
    document.body.classList.remove("smooth-dnd-no-user-select");
    document.querySelectorAll(".smooth-dnd-ghost").forEach((ghost) => {
      ghost.remove();
    });
    removeKanbanDragPreview();
  };

  useEffect(() => {
    let safetyTimer = null;

    const onPointerRelease = () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }
      safetyTimer = window.setTimeout(() => {
        if (
          document.body.classList.contains("smooth-dnd-disable-touch-action") ||
          document.body.classList.contains("smooth-dnd-no-user-select")
        ) {
          cleanupStuckDrag();
        } else {
          document.body.classList.remove("kanban-dragging");
        }
      }, 600);
    };

    document.addEventListener("mouseup", onPointerRelease);
    document.addEventListener("touchend", onPointerRelease);

    return () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }
      document.removeEventListener("mouseup", onPointerRelease);
      document.removeEventListener("touchend", onPointerRelease);
      cleanupStuckDrag();
    };
  }, []);

  const handleCardDragStart = () => {
    document.body.classList.add("kanban-dragging");
    const state = dragPreviewStateRef.current;
    if (!state.article) {
      return;
    }
    state.isDragging = true;
    ensureKanbanDragPreview();
  };

  const handleCardDragEnd = () => {
    document.body.classList.remove("kanban-dragging");
    removeKanbanDragPreview();
    return true;
  };

  useEffect(() => {
    fetchTags();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete" ||
        data.action === "reorder"
      ) {
        fetchTags();
      }
    };

    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [socket, user.companyId]);

  const fetchTickets = async (withDateFilter = applyDateFilter) => {
    try {
      const params = {
        queueIds: JSON.stringify(jsonString),
      };

      if (withDateFilter && startDate && endDate) {
        params.dateStart = startDate;
        params.dateEnd = endDate;
      }

      const { data } = await api.get("/ticket/kanban", { params });
      setTickets(data.tickets || []);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      if (skipSocketFetchRef.current) {
        return;
      }
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, applyDateFilter, startDate, endDate]);

  const handleSearchClick = () => {
    setApplyDateFilter(true);
    fetchTickets(true);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleCardClick = (uuid) => {
    history.push("/tickets/" + uuid);
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook className={classes.channelIcon} style={{ color: "#3b5998" }} />;
      case "instagram":
        return <Instagram className={classes.channelIcon} style={{ color: "#e1306c" }} />;
      case "whatsapp":
        return <WhatsApp className={classes.channelIcon} style={{ color: "#25d366" }} />;
      default:
        return null;
    }
  };

  const popularCards = () => {
    const kanbanTagIds = new Set(tags.map((tag) => tag.id));
    const hasKanbanStage = (ticket) =>
      (ticket.tags || []).some((tag) => kanbanTagIds.has(tag.id));
    const filteredTickets = tickets.filter((ticket) => !hasKanbanStage(ticket));

    const lanes = [
      {
        id: "lane0",
        title: i18n.t("tagsKanban.laneDefault"),
        label: filteredTickets.length.toString(),
        cards: filteredTickets.map(ticket =>
          buildTicketCard(ticket, classes, handleCardClick, IconChannel, null, isLight)
        ),
      },
      ...tags.map(tag => {
        const taggedTickets = tickets.filter((ticket) =>
          (ticket.tags || []).some((ticketTag) => ticketTag.id === tag.id)
        );

        return {
          id: tag.id.toString(),
          title: tag.name,
          label: taggedTickets?.length.toString(),
          cards: taggedTickets.map(ticket =>
            buildTicketCard(ticket, classes, handleCardClick, IconChannel, tag.color, isLight)
          ),
          style: getLaneGlassStyle(tag.color, isLight),
          titleStyle: { color: tag.color, fontWeight: 700 },
          labelStyle: {
            color: isLight ? "#64748b" : "#94a3b8",
            fontSize: 12,
          },
        };
      }),
    ];

    setFile({ lanes });
  };

  useEffect(() => {
    popularCards();
  }, [tags, tickets]);

  const handleBoardDataChange = (newData) => {
    setFile(newData);
  };

  const handleCardMove = async (fromLaneId, toLaneId, cardId) => {
    if (fromLaneId === toLaneId) {
      return;
    }

    skipSocketFetchRef.current = true;
    setTickets((prevTickets) =>
      applyTicketStageMove(prevTickets, cardId, toLaneId, tags)
    );

    try {
      await api.delete(`/ticket-tags/${cardId}`);

      if (toLaneId !== "lane0") {
        await api.put(`/ticket-tags/${cardId}/${toLaneId}`);
      }
    } catch (err) {
      toastError(err);
      await fetchTickets();
    } finally {
      window.setTimeout(() => {
        skipSocketFetchRef.current = false;
      }, 2000);
    }
  };

  const handleAddConnectionClick = () => {
    history.push('/tagsKanban');
  };

  if (!canUserAccessKanban(user)) {
    return <ForbiddenPage />;
  }

  return (
    <div className={`${classes.root} kanban-wrapper`}>
      <div className={classes.toolbar}>
        <div className={classes.toolbarFilters}>
          <TextField
            label={i18n.t("kanban.startDate")}
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            className={classes.dateInput}
          />
          {!isMobile && <Box mx={1} />}
          <TextField
            label={i18n.t("kanban.endDate")}
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            className={classes.dateInput}
          />
          {!isMobile && <Box mx={1} />}
          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "small" : "medium"}
            className={classes.searchButton}
            onClick={handleSearchClick}
          >
            {i18n.t("kanban.search")}
          </Button>
        </div>
        <Can role={user.profile} perform="dashboard:view" yes={() => (
          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "small" : "medium"}
            className={classes.addColumnsButton}
            onClick={handleAddConnectionClick}
          >
            {i18n.t("kanban.addColumns")}
          </Button>
        )} />
      </div>
      <div className={`${classes.kanbanScrollContainer} kanban-scroll-container`}>
        <div className={classes.kanbanColumns}>
          <Board
            key={`kanban-${tags.map((tag) => tag.id).join("-") || "default"}`}
            data={file}
            draggable
            onCardMoveAcrossLanes={handleCardMove}
            onDataChange={handleBoardDataChange}
            handleDragStart={handleCardDragStart}
            handleDragEnd={handleCardDragEnd}
            getGhostParent={() => document.body}
            cardDragClass="kanban-card-dragging"
            laneStyle={{
              width: 300,
              minWidth: 300,
              maxWidth: 300,
              flexShrink: 0,
            }}
            style={{
              backgroundColor: "transparent",
              overflow: "visible",
              height: "100%",
              maxHeight: "100%",
              minHeight: 0,
              padding: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Kanban;
